/* eslint-disable no-param-reassign */
import { useRef } from "react";

/**
 * The browser spec does not support transitioning (animating) between auto heights. This hook programmatically implements auto height transitioning.
 */
export const useAutoTransitionHeight = <TElement extends HTMLElement>({ initiallyExpanded }: { initiallyExpanded: boolean }) => {
    const elementRef = useRef<TElement>();
    const expandedRef = useRef<boolean>(false);

    const setElementRef = (element: TElement) => {
        if (element === null) {
            return;
        }
        const firstRender = elementRef.current === undefined;
        elementRef.current = element;
        if (firstRender) {
            setExpanded(initiallyExpanded);
        }
    };

    const setExpanded = (expanded: boolean) => {
        if (elementRef.current == null) {
            return;
        }

        const element = elementRef.current;
        if (expanded) {
            const startingDimensions = element.getBoundingClientRect();
            element.style.height = "auto";
            const dimensions = element.getBoundingClientRect();
            element.style.height = `${startingDimensions.height}px`;
            element.style.visibility = "visible";
            // Set timeout to allow the browser to do layout changes triggered by the above style modifications.
            // Without this, the browser will batch the changes and the user will not see a smooth transition.
            setTimeout(() => {
                element.style.height = `${dimensions.height}px`;
            });
        } else {
            element.style.height = "0px";
            element.style.visibility = "hidden";
        }
        expandedRef.current = expanded;
    };

    return { setExpanded, setElementRef };
};
